import { Box, ThemeProvider, useTheme } from '@mui/material';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PropsWithChildren, ReactElement, ReactNode } from 'react';

const c = tokens.color;

export interface PaloHistoryProps {
    /**
     * Leave out where the content heads itself — the activity stream brings its
     * own title, count and filters, and a second heading above it only repeats
     * the word.
     */
    title?: ReactNode;
    /** How many entries there are; shown next to the title, none for zero. */
    count?: number;
    /** Right-aligned affordances next to the heading, e.g. sending a mail. */
    actions?: ReactNode;
    /**
     * Sits above the history. A remark that is not tied to a logged change
     * belongs here — today one can only comment on something the system has
     * already recorded (Stefan Graf, Besprechung 2026-09-03).
     */
    composer?: ReactNode;
}

/**
 * The document's history, at the foot of the mask and across its full width.
 *
 * It used to share the right-hand column with everything else, roughly 350
 * pixels for entries that are whole sentences. Down here it has the width of
 * the form, which is what makes it readable; one scrolls to it when one wants
 * it, and it is out of the way while the form is being filled.
 */
export const PaloHistory = ({
    title,
    count,
    actions,
    composer,
    children,
}: PropsWithChildren<PaloHistoryProps>): ReactElement => {
    const ambientTheme = useTheme();

    return (
        <Box sx={{ borderTop: `1px solid ${c.line}`, backgroundColor: c.surface, padding: '14px 16px 20px' }}>
            {composer && (
                <ThemeProvider theme={ambientTheme}>
                    <Box sx={{ mb: '16px' }}>{composer}</Box>
                </ThemeProvider>
            )}

            {(title || actions) && (
                <PaloThemeProvider>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            pb: '6px',
                            mb: '10px',
                            borderBottom: `1px solid ${c.line}`,
                            fontSize: '10.5px',
                            fontWeight: 640,
                            letterSpacing: '.08em',
                            textTransform: 'uppercase',
                            color: c.ink3,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>{title}</Box>
                        {Boolean(count) && (
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 500,
                                    letterSpacing: 'normal',
                                    textTransform: 'none',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {count}
                            </Box>
                        )}
                        {actions && <Box sx={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>{actions}</Box>}
                    </Box>
                </PaloThemeProvider>
            )}

            <ThemeProvider theme={ambientTheme}>
                <Box sx={{ '& *': { minWidth: 0 } }}>{children}</Box>
            </ThemeProvider>
        </Box>
    );
};
